import {
  getAllIndexedChannelCosts,
  type ChannelCostIndex,
  type ChannelCostMatchCriteria,
  type ChannelCostRule,
  getIndexedChannelCost,
} from './channelCostIndex';
import { formatCurrency } from '../../../lib/format';
import type { ChannelCostSources } from './channelCost';
import type { ChannelCostEstimateId } from '@seaber/common/graphql/types';

/**
 * Generates a compound key for a channel cost rule.
 * Format: `{vesselId|vesselType|default}/{assortmentGroupId|null}/{cargoCondition|null}/{ruleId}`
 */
export function getChannelCostKey(rule: ChannelCostRule): string {
  const vesselPart =
    rule.vesselId !== null
      ? `vid-${rule.vesselId}`
      : rule.vesselTypeName !== null
        ? `vt-${rule.vesselTypeName}`
        : 'default';

  const assortmentPart =
    rule.assortmentGroupId !== null
      ? `ag-${rule.assortmentGroupId}`
      : 'ag-null';

  const cargoPart =
    rule.cargoCondition !== null ? `cc-${rule.cargoCondition}` : 'cc-null';

  return `${vesselPart}/${assortmentPart}/${cargoPart}/${rule.id}`;
}

/**
 * Parses a channel cost key back to its components
 */
export function parseChannelCostKey(key: string): {
  vesselPart: string;
  assortmentPart: string;
  cargoPart: string;
  ruleId: ChannelCostEstimateId;
} | null {
  const parts = key.split('/');
  if (parts.length !== 4) return null;

  const [vesselPart, assortmentPart, cargoPart, ruleIdStr] = parts;
  const ruleId = parseInt(ruleIdStr, 10) as ChannelCostEstimateId;

  if (isNaN(ruleId)) return null;

  return { vesselPart, assortmentPart, cargoPart, ruleId };
}

/**
 * Creates a label for a channel cost rule
 */
function getChannelCostRuleLabel(rule: ChannelCostRule): string {
  const parts: string[] = [];

  if (rule.vesselId !== null) {
    parts.push(`Use default for ${rule.vesselName}`);
  } else if (rule.vesselTypeName !== null) {
    parts.push(`Use default for ${rule.vesselTypeName}`);
  } else {
    parts.push(`Use default for ${rule.channelName}`);
  }

  if (rule.assortmentGroupId !== null) {
    parts.push(`Commodity Group ${rule.assortmentGroupName}`);
  }

  if (rule.cargoCondition !== null) {
    parts.push(rule.cargoCondition.toLowerCase());
  }

  const costStr = formatCurrency(rule.channelCost, 0, rule.channelCostCurrency);

  return `${parts.join(' » ')} - ${costStr}`;
}

/**
 * Converts a ChannelCostRule to a ChannelCostSelectOption
 */
export function ruleToSelectOption(rule: ChannelCostRule): {
  label: string;
  value: string;
} {
  return {
    label: getChannelCostRuleLabel(rule),
    value: getChannelCostKey(rule),
  };
}

export const ONE_TIME_OVERRIDE_KEY = 'oneTimeOverride';

/**
 * Result of calculating the effective channel cost
 */
export type GetEffectiveChannelCostResult = {
  /** Total cost amount */
  cost: number;
  /** Total duration overhead in hours */
  durationOverhead: number;
  /** The key of the selected/active option */
  selectedKey: string | null;
  /** All available sources */
  sources: ChannelCostSources;
};

export function getEffectiveChannelCost(params: {
  /** Used channel names */
  channelNames: string[];
  /** One-time override value if set */
  oneTimeOverrideChannelCost: number | null | undefined;
  /** Manually selected channel cost default ID */
  channelCostDefaultId: number | null | undefined;
  /** Channel cost index */
  channelCostIndex: ChannelCostIndex;
  /** Lookup map for rules by ID (for manual selection) */
  channelCostRuleById: Map<number, ChannelCostRule>;
  /** Cost matching criteria, excluding channel */
  baseCriteria: Omit<ChannelCostMatchCriteria, 'channelName'>;
}): GetEffectiveChannelCostResult {
  const {
    channelNames,
    oneTimeOverrideChannelCost,
    channelCostDefaultId,
    channelCostIndex,
    channelCostRuleById,
    baseCriteria,
  } = params;

  const allMatchingRules = channelNames.flatMap((channelName) => {
    const criteria: ChannelCostMatchCriteria = {
      ...baseCriteria,
      channelName,
    };
    return getAllIndexedChannelCosts(channelCostIndex, criteria);
  });

  // Handle one-time override
  if (oneTimeOverrideChannelCost != null) {
    return {
      cost: oneTimeOverrideChannelCost,
      durationOverhead: 0,
      selectedKey: ONE_TIME_OVERRIDE_KEY,
      sources: {
        oneTimeOverride: oneTimeOverrideChannelCost,
        channelCostDefaultId: null,
        matchingChannelCosts: allMatchingRules,
      },
    };
  }

  // Handle manual selection
  if (channelCostDefaultId != null) {
    const selectedRule = channelCostRuleById.get(channelCostDefaultId);

    if (selectedRule) {
      return {
        cost: selectedRule.channelCost,
        durationOverhead: selectedRule.durationOverhead ?? 0,
        selectedKey: getChannelCostKey(selectedRule),
        sources: {
          oneTimeOverride: null,
          channelCostDefaultId,
          matchingChannelCosts: allMatchingRules,
        },
      };
    }
  }

  // Fall back to index matching
  let totalCost = 0;
  let totalDurationOverhead = 0;
  let firstMatchedRule: ChannelCostRule | null = null;
  let firstMatchedKey: string | null = null;

  for (const channelName of channelNames) {
    const criteria: ChannelCostMatchCriteria = { ...baseCriteria, channelName };

    // Get the best match for cost calculation
    const bestMatch = getIndexedChannelCost(channelCostIndex, criteria);

    if (bestMatch) {
      totalCost += bestMatch.channelCost;
      totalDurationOverhead += bestMatch.durationOverhead ?? 0;
      if (!firstMatchedRule) {
        firstMatchedRule = bestMatch;
        firstMatchedKey = getChannelCostKey(bestMatch);
      }
    }
  }

  return {
    cost: totalCost,
    durationOverhead: totalDurationOverhead,
    selectedKey: firstMatchedKey,
    sources: {
      oneTimeOverride: null,
      channelCostDefaultId: null,
      matchingChannelCosts: allMatchingRules,
    },
  };
}
